/**
 * HSL Insights — pure analytics over fixtures. No Supabase calls here so every
 * number on /insights can be unit tested.
 *
 * League rules applied (same as the database trigger `recompute_team_standings`
 * and `buildStandingsFromFixtures`):
 * - A fixture counts once `game_played` is true and it is a forfeit or has both scores.
 * - Win 3, draw 1, loss 0.
 * - Forfeit: both teams get 0 points and no W/D/L; it counts as a game played and
 *   contributes no goals for/against. Forfeits are excluded from match and player stats.
 * - Ranking: points, then goal difference, then goals for. Teams level on all three
 *   share a position.
 *
 * Matchdays: there is no matchday column, so a matchday is one distinct fixture
 * date within the season + division schedule (played or not), numbered in date order.
 */

export const POINTS = { win: 3, draw: 1, loss: 0, forfeit: 0 };

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

/** "completed" | "forfeit" | "pending" | "incomplete" (marked played but a score is missing). */
export function fixtureStatus(f) {
  if (!f || f.game_played !== true) return "pending";
  if (f.is_forfeit === true) return "forfeit";
  if (num(f.home_team_score) === null || num(f.away_team_score) === null) return "incomplete";
  return "completed";
}

export const countsForStandings = (f) => {
  const s = fixtureStatus(f);
  return s === "completed" || s === "forfeit";
};

const fixtureOrder = (a, b) =>
  String(a.date).localeCompare(String(b.date)) ||
  String(a.time || "").localeCompare(String(b.time || "")) ||
  (a.id || 0) - (b.id || 0);

export function buildMatchdays(fixtures) {
  const dates = [...new Set((fixtures || []).map((f) => f.date).filter(Boolean))].sort();
  const matchdays = dates.map((date, i) => ({
    matchday: i + 1,
    date,
    fixtures: (fixtures || []).filter((f) => f.date === date).sort(fixtureOrder),
  }));
  const mdByFixtureId = {};
  matchdays.forEach((md) => md.fixtures.forEach((f) => (mdByFixtureId[f.id] = md.matchday)));
  return { matchdays, mdByFixtureId };
}

/** Highest matchday that has at least one fixture counting toward the standings. */
export function latestReportableMatchday(matchdays) {
  let latest = 0;
  for (const md of matchdays) if (md.fixtures.some(countsForStandings)) latest = md.matchday;
  return latest;
}

function emptyRow(team) {
  return {
    teamId: team.id,
    name: team.name || `Team #${team.id}`,
    logoUrl: team.logo_url || null,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    forfeits: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: 0,
    cleanSheets: 0,
  };
}

export function compareRows(a, b) {
  return (
    b.points - a.points ||
    b.goalDifference - a.goalDifference ||
    b.goalsFor - a.goalsFor ||
    String(a.name).localeCompare(String(b.name))
  );
}

const levelOnTieBreaks = (a, b) =>
  a.points === b.points && a.goalDifference === b.goalDifference && a.goalsFor === b.goalsFor;

/** Assigns `position` (shared when level on points, GD and GF) and `tied`. */
export function rankRows(rows) {
  const sorted = [...rows].sort(compareRows);
  sorted.forEach((row, i) => {
    const prev = sorted[i - 1];
    row.position = prev && levelOnTieBreaks(prev, row) ? prev.position : i + 1;
  });
  sorted.forEach((row) => {
    row.tied = sorted.some((o) => o !== row && o.position === row.position);
  });
  return sorted;
}

/** Standings using only fixtures on matchdays 1..uptoMatchday. */
export function standingsAtMatchday(fixtures, teams, mdByFixtureId, uptoMatchday) {
  const rows = new Map();
  (teams || []).forEach((t) => rows.set(t.id, emptyRow(t)));
  const ensure = (id) => {
    if (!rows.has(id)) rows.set(id, emptyRow({ id }));
    return rows.get(id);
  };

  for (const f of fixtures || []) {
    const md = mdByFixtureId[f.id];
    if (!md || md > uptoMatchday || !countsForStandings(f)) continue;
    if (f.home_team_id == null || f.away_team_id == null) continue;
    const h = ensure(f.home_team_id);
    const a = ensure(f.away_team_id);
    h.played += 1;
    a.played += 1;
    if (fixtureStatus(f) === "forfeit") {
      h.forfeits += 1;
      a.forfeits += 1;
      continue;
    }
    const hs = num(f.home_team_score);
    const as = num(f.away_team_score);
    h.goalsFor += hs;
    h.goalsAgainst += as;
    a.goalsFor += as;
    a.goalsAgainst += hs;
    if (as === 0) h.cleanSheets += 1;
    if (hs === 0) a.cleanSheets += 1;
    if (hs > as) {
      h.won += 1;
      a.lost += 1;
    } else if (hs < as) {
      a.won += 1;
      h.lost += 1;
    } else {
      h.drawn += 1;
      a.drawn += 1;
    }
  }

  for (const r of rows.values()) {
    r.goalDifference = r.goalsFor - r.goalsAgainst;
    r.points = r.won * POINTS.win + r.drawn * POINTS.draw;
  }
  return rankRows([...rows.values()]);
}

/** Cumulative points and table position per team for matchdays 1..uptoMatchday. */
export function pointsHistory(fixtures, teams, mdByFixtureId, uptoMatchday) {
  const history = {};
  (teams || []).forEach((t) => (history[t.id] = { points: [], positions: [] }));
  for (let md = 1; md <= uptoMatchday; md += 1) {
    const table = standingsAtMatchday(fixtures, teams, mdByFixtureId, md);
    table.forEach((row) => {
      if (!history[row.teamId]) history[row.teamId] = { points: [], positions: [] };
      history[row.teamId].points.push(row.points);
      history[row.teamId].positions.push(row.position);
    });
  }
  return history;
}

/** Results for one team in matchday order: "W" | "D" | "L" | "F". */
export function teamResults(teamId, fixtures, mdByFixtureId, uptoMatchday) {
  return (fixtures || [])
    .filter(
      (f) =>
        (f.home_team_id === teamId || f.away_team_id === teamId) &&
        countsForStandings(f) &&
        mdByFixtureId[f.id] &&
        mdByFixtureId[f.id] <= uptoMatchday
    )
    .sort(fixtureOrder)
    .map((f) => {
      if (fixtureStatus(f) === "forfeit") return "F";
      const isHome = f.home_team_id === teamId;
      const us = num(isHome ? f.home_team_score : f.away_team_score);
      const them = num(isHome ? f.away_team_score : f.home_team_score);
      return us > them ? "W" : us < them ? "L" : "D";
    });
}

/** Current run from the most recent result backwards. A forfeit ends every run. */
export function currentRun(results, accepts) {
  let n = 0;
  for (let i = results.length - 1; i >= 0; i -= 1) {
    if (!accepts.includes(results[i])) break;
    n += 1;
  }
  return n;
}

/** All entries sharing the best value. Empty when nothing qualifies. */
export function leaders(items, value, { min = -Infinity, lowest = false } = {}) {
  const eligible = items.filter((it) => {
    const v = value(it);
    return v !== null && v !== undefined && !Number.isNaN(v) && v >= min;
  });
  if (eligible.length === 0) return { value: null, entries: [] };
  const best = eligible.reduce((acc, it) => {
    const v = value(it);
    return lowest ? Math.min(acc, v) : Math.max(acc, v);
  }, lowest ? Infinity : -Infinity);
  return { value: best, entries: eligible.filter((it) => value(it) === best) };
}

// ---------------------------------------------------------------------------
// Scorers
// ---------------------------------------------------------------------------

/** `{ "<playerId>": goals }` → [{ playerId, goals }] (positive integers only). */
export function parseScorers(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw)
    .map(([playerId, goals]) => ({ playerId: String(playerId), goals: Number(goals) }))
    .filter((s) => Number.isFinite(s.goals) && s.goals > 0);
}

/**
 * Per side of each completed fixture: recorded scorer goals vs the score.
 * `unattributed` goals have no scorer recorded; `overRecorded` means scorers
 * add up to more than the score (bad data).
 */
export function scorerAttribution(fixtures) {
  const sides = [];
  for (const f of fixtures || []) {
    if (fixtureStatus(f) !== "completed") continue;
    [
      ["home", f.home_team_id, f.home_team_score, f.home_team_scorers],
      ["away", f.away_team_id, f.away_team_score, f.away_team_scorers],
    ].forEach(([side, teamId, score, raw]) => {
      const scorers = parseScorers(raw);
      const recorded = scorers.reduce((s, x) => s + x.goals, 0);
      const goals = num(score) || 0;
      sides.push({
        fixtureId: f.id,
        side,
        teamId,
        goals,
        scorers,
        recorded,
        unattributed: Math.max(0, goals - recorded),
        overRecorded: recorded > goals,
      });
    });
  }
  return sides;
}

/**
 * Top scorer(s) across the given fixtures, with a certainty check: missing scorer
 * records are allowed only if they could not change who leads. Each team's
 * unattributed goals could all belong to any one player of that team (or to an
 * unrecorded player), so the leader is confirmed only when no other player could
 * reach their total.
 */
export function topScorers(fixtures, playersById = {}) {
  const sides = scorerAttribution(fixtures);
  const totals = new Map();
  const unattributedByTeam = new Map();
  let overRecorded = false;

  for (const s of sides) {
    if (s.overRecorded) overRecorded = true;
    unattributedByTeam.set(s.teamId, (unattributedByTeam.get(s.teamId) || 0) + s.unattributed);
    for (const sc of s.scorers) {
      const cur = totals.get(sc.playerId) || { playerId: sc.playerId, goals: 0, teamId: s.teamId };
      cur.goals += sc.goals;
      totals.set(sc.playerId, cur);
    }
  }

  const players = [...totals.values()].map((p) => ({
    ...p,
    name: playersById[p.playerId]?.name || "Unknown",
    jerseyNumber: playersById[p.playerId]?.jersey_number ?? null,
  }));
  const totalUnattributed = [...unattributedByTeam.values()].reduce((a, b) => a + b, 0);
  const top = leaders(players, (p) => p.goals, { min: 1 });

  if (top.entries.length === 0 || overRecorded) {
    return { ...top, confirmed: false, totalUnattributed, overRecorded, players };
  }

  const leaderIds = new Set(top.entries.map((p) => p.playerId));
  let maxOther = 0;
  for (const p of players) {
    if (leaderIds.has(p.playerId)) continue;
    maxOther = Math.max(maxOther, p.goals + (unattributedByTeam.get(p.teamId) || 0));
  }
  for (const u of unattributedByTeam.values()) maxOther = Math.max(maxOther, u);

  const tiedLeaderCouldPullAhead =
    top.entries.length > 1 && top.entries.some((p) => (unattributedByTeam.get(p.teamId) || 0) > 0);

  const confirmed = top.value > maxOther && !tiedLeaderCouldPullAhead;
  return { ...top, confirmed, totalUnattributed, overRecorded, players };
}

/**
 * The top `places` scorers by recorded goals, with shared ranks for ties.
 * `tiedBeyond` counts players level with the last one shown who didn't fit.
 * `leaderConfirmed` mirrors topScorers' certainty check.
 */
export function scorerRace(scorers, places = 3) {
  if (scorers.overRecorded)
    return { entries: [], tiedBeyond: 0, leaderConfirmed: false, totalUnattributed: scorers.totalUnattributed };
  const sorted = [...scorers.players]
    .filter((p) => p.goals > 0)
    .sort((a, b) => b.goals - a.goals || a.name.localeCompare(b.name));
  sorted.forEach((p, i) => {
    p.rank = i > 0 && sorted[i - 1].goals === p.goals ? sorted[i - 1].rank : i + 1;
  });
  const entries = sorted.slice(0, places);
  const last = entries[entries.length - 1];
  return {
    entries,
    tiedBeyond: last ? sorted.slice(places).filter((p) => p.goals === last.goals).length : 0,
    leaderConfirmed: scorers.confirmed,
    totalUnattributed: scorers.totalUnattributed,
  };
}

// ---------------------------------------------------------------------------
// Matchday match stats
// ---------------------------------------------------------------------------

export function matchStats(fixtures, teamsById = {}) {
  const completed = (fixtures || []).filter((f) => fixtureStatus(f) === "completed");
  const describe = (f) => {
    const hs = num(f.home_team_score);
    const as = num(f.away_team_score);
    return {
      fixtureId: f.id,
      homeTeamId: f.home_team_id,
      awayTeamId: f.away_team_id,
      homeName: teamsById[f.home_team_id]?.name || "Home",
      awayName: teamsById[f.away_team_id]?.name || "Away",
      homeScore: hs,
      awayScore: as,
      totalGoals: hs + as,
      margin: Math.abs(hs - as),
      winnerTeamId: hs > as ? f.home_team_id : as > hs ? f.away_team_id : null,
      maxTeamGoals: Math.max(hs, as),
    };
  };
  const matches = completed.map(describe);
  const counts = { completed: 0, forfeit: 0, pending: 0, incomplete: 0 };
  (fixtures || []).forEach((f) => (counts[fixtureStatus(f)] += 1));

  return {
    counts,
    matches,
    totalGoals: matches.reduce((s, m) => s + m.totalGoals, 0),
    draws: matches.filter((m) => m.homeScore === m.awayScore).length,
    highestScoring: leaders(matches, (m) => m.totalGoals, { min: 1 }),
    biggestWin: leaders(matches, (m) => m.margin, { min: 1 }),
    mostGoalsOneTeam: leaders(
      matches.flatMap((m) => [
        { ...m, teamId: m.homeTeamId, teamName: m.homeName, goals: m.homeScore },
        { ...m, teamId: m.awayTeamId, teamName: m.awayName, goals: m.awayScore },
      ]),
      (x) => x.goals,
      { min: 1 }
    ),
  };
}

// ---------------------------------------------------------------------------
// Full weekly report
// ---------------------------------------------------------------------------

/**
 * @param fixtures all fixtures of one season + city + division (any status)
 * @param teams    [{ id, name, logo_url }]
 * @param players  [{ id, name, jersey_number }] for scorer lookups
 * @param matchday selected matchday (defaults to the latest with results)
 * @param official current `teams` rows (wins/draws/losses/forfeits/goalsFor/goalsAgainst) to validate against
 */
export function buildWeeklyReport({ fixtures = [], teams = [], players = [], matchday, official = [] }) {
  const { matchdays, mdByFixtureId } = buildMatchdays(fixtures);
  const latest = latestReportableMatchday(matchdays);
  const selected = Math.min(Math.max(1, Number(matchday) || latest || 1), Math.max(1, matchdays.length));
  const teamsById = Object.fromEntries(teams.map((t) => [t.id, t]));
  const playersById = Object.fromEntries(players.map((p) => [String(p.id), p]));

  const mdInfo = matchdays.find((m) => m.matchday === selected) || null;
  const mdFixtures = mdInfo ? mdInfo.fixtures : [];
  const hasResults = mdFixtures.some(countsForStandings);

  const standings = standingsAtMatchday(fixtures, teams, mdByFixtureId, selected);
  const previous = selected > 1 ? standingsAtMatchday(fixtures, teams, mdByFixtureId, selected - 1) : null;
  const history = pointsHistory(fixtures, teams, mdByFixtureId, selected);

  const seasonFixtures = fixtures.filter((f) => mdByFixtureId[f.id] && mdByFixtureId[f.id] <= selected);

  const teamRows = standings.map((row) => {
    const results = teamResults(row.teamId, fixtures, mdByFixtureId, selected);
    const prevRow = previous?.find((p) => p.teamId === row.teamId);
    const mdPoints = mdFixtures
      .filter((f) => countsForStandings(f) && (f.home_team_id === row.teamId || f.away_team_id === row.teamId))
      .reduce((s, f) => {
        if (fixtureStatus(f) === "forfeit") return s;
        const isHome = f.home_team_id === row.teamId;
        const us = num(isHome ? f.home_team_score : f.away_team_score);
        const them = num(isHome ? f.away_team_score : f.home_team_score);
        return s + (us > them ? POINTS.win : us === them ? POINTS.draw : 0);
      }, 0);
    const playedThisMatchday = mdFixtures.some(
      (f) => countsForStandings(f) && (f.home_team_id === row.teamId || f.away_team_id === row.teamId)
    );
    return {
      ...row,
      results,
      form: results.slice(-5),
      winningRun: currentRun(results, ["W"]),
      unbeatenRun: currentRun(results, ["W", "D"]),
      previousPosition: prevRow ? prevRow.position : null,
      positionChange: prevRow ? prevRow.position - row.position : null,
      matchdayPoints: mdPoints,
      playedThisMatchday,
      goalsAgainstPerGame:
        row.played - row.forfeits > 0 ? row.goalsAgainst / (row.played - row.forfeits) : null,
    };
  });

  const withGames = teamRows.filter((r) => r.played - r.forfeits > 0);
  const teamStats = {
    mostGoalsFor: leaders(withGames, (r) => r.goalsFor, { min: 1 }),
    mostGoalsAgainst: leaders(withGames, (r) => r.goalsAgainst, { min: 1 }),
    bestDefence: leaders(withGames, (r) => r.goalsAgainstPerGame, { lowest: true }),
    bestGoalDifference: leaders(withGames, (r) => r.goalDifference),
    mostMatchdayPoints: leaders(teamRows.filter((r) => r.playedThisMatchday), (r) => r.matchdayPoints, { min: 1 }),
    biggestClimber: previous
      ? leaders(teamRows, (r) => r.positionChange, { min: 1 })
      : { value: null, entries: [] },
    longestWinningRun: leaders(teamRows, (r) => r.winningRun, { min: 2 }),
    longestUnbeatenRun: leaders(teamRows, (r) => r.unbeatenRun, { min: 2 }),
    mostCleanSheets: leaders(withGames, (r) => r.cleanSheets, { min: 1 }),
  };

  const matchdayMatches = matchStats(mdFixtures, teamsById);
  const seasonMatches = matchStats(seasonFixtures, teamsById);

  const goldenBoot = topScorers(seasonFixtures, playersById);
  const goldenBootRace = scorerRace(goldenBoot);
  const matchdayTopScorer = topScorers(mdFixtures, playersById);

  const validation = validateAgainstOfficial({
    fixtures,
    teams,
    mdByFixtureId,
    official,
    selected,
    latest,
  });

  const series = teams
    .slice()
    .sort((a, b) => a.id - b.id)
    .map((t, i) => ({
      teamId: t.id,
      name: t.name,
      logoUrl: t.logo_url || null,
      color: TEAM_COLORS[i % TEAM_COLORS.length],
      points: history[t.id]?.points || [],
    }));

  return {
    matchdays: matchdays.map(({ matchday: m, date, fixtures: fx }) => ({
      matchday: m,
      date,
      fixtureCount: fx.length,
      reportable: fx.some(countsForStandings),
    })),
    latestMatchday: latest,
    matchday: selected,
    matchdayDate: mdInfo?.date || null,
    hasResults,
    standings: teamRows,
    previousStandings: previous,
    series,
    leaderIds: teamRows.filter((r) => r.position === 1 && r.played > 0).map((r) => r.teamId),
    matchdayFixtures: mdFixtures.map((f) => ({
      id: f.id,
      time: f.time,
      status: fixtureStatus(f),
      homeTeamId: f.home_team_id,
      awayTeamId: f.away_team_id,
      homeScore: num(f.home_team_score),
      awayScore: num(f.away_team_score),
    })),
    teamStats,
    matchdayMatches,
    seasonMatches,
    goldenBoot,
    goldenBootRace,
    matchdayTopScorer,
    validation,
  };
}

/**
 * Compares the reconstruction through the latest results with the stored team
 * columns (kept up to date by the database trigger). Only meaningful for the
 * latest matchday; earlier matchdays are reconstructed from the same fixtures.
 */
export function validateAgainstOfficial({ fixtures, teams, mdByFixtureId, official, latest }) {
  if (!official || official.length === 0 || !latest) return { checked: false, mismatches: [] };
  const recon = standingsAtMatchday(fixtures, teams, mdByFixtureId, Number.MAX_SAFE_INTEGER);
  const byId = Object.fromEntries(official.map((o) => [o.id, o]));
  const mismatches = [];
  for (const r of recon) {
    const o = byId[r.teamId];
    if (!o) continue;
    const fields = [
      ["won", o.wins],
      ["drawn", o.draws],
      ["lost", o.losses],
      ["forfeits", o.forfeits],
      ["goalsFor", o.goalsFor],
      ["goalsAgainst", o.goalsAgainst],
    ];
    const diffs = fields
      .filter(([k, v]) => v !== null && v !== undefined && Number(v) !== r[k])
      .map(([k, v]) => ({ field: k, official: Number(v), reconstructed: r[k] }));
    if (diffs.length) mismatches.push({ teamId: r.teamId, name: r.name, diffs });
  }
  return { checked: true, mismatches };
}

// ---------------------------------------------------------------------------
// Highlight selection for the poster
// ---------------------------------------------------------------------------

const teamEntity = (r) => ({ id: `t${r.teamId}`, name: r.name, logoUrl: r.logoUrl });

/** All candidate highlight cards, most interesting first. */
export function highlightCandidates(report, teamsById = {}) {
  const out = [];
  const ts = report.teamStats;
  const add = (key, label, tone, value, detail, entities, weight) => {
    if (!entities || entities.length === 0 || value === null || value === undefined) return;
    out.push({ key, label, tone, value, detail, entities, weight });
  };
  const fmtMatch = (m) => `${m.homeName} ${m.homeScore}–${m.awayScore} ${m.awayName}`;

  const race = report.goldenBootRace;
  if (race && race.entries.length) {
    add(
      "golden-boot",
      "GOLDEN BOOT RACE",
      "gold",
      race.entries[0].goals,
      [
        race.tiedBeyond ? `+${race.tiedBeyond} more on ${race.entries[race.entries.length - 1].goals}` : null,
        race.leaderConfirmed ? null : `${race.totalUnattributed} goal${race.totalUnattributed === 1 ? "" : "s"} without a scorer`,
      ]
        .filter(Boolean)
        .join(" · ") || "goals",
      race.entries.map((p) => ({
        id: `p${p.playerId}`,
        name: p.name,
        logoUrl: teamsById[p.teamId]?.logo_url || null,
        sub: teamsById[p.teamId]?.name || "",
        rank: p.rank,
        goals: p.goals,
      })),
      95
    );
  }

  const mts = report.matchdayTopScorer;
  if (mts.confirmed && mts.value >= 2 && report.matchday > 1) {
    add(
      "matchday-scorer",
      mts.value >= 3 ? "HAT-TRICK HERO" : "MATCHDAY MARKSMAN",
      "gold",
      mts.value,
      "goals this matchday",
      mts.entries.map((p) => ({
        id: `p${p.playerId}`,
        name: p.name,
        logoUrl: teamsById[p.teamId]?.logo_url || null,
        sub: teamsById[p.teamId]?.name || "",
      })),
      mts.value >= 3 ? 92 : 60
    );
  }

  const fest = report.matchdayMatches.highestScoring;
  if (fest.value >= 5) {
    add(
      "goal-fest",
      "MATCHDAY GOAL FEST",
      "gold",
      fest.value,
      fest.entries.map(fmtMatch).join(" · "),
      fest.entries.flatMap((m) => [
        { id: `t${m.homeTeamId}`, name: m.homeName, logoUrl: teamsById[m.homeTeamId]?.logo_url || null },
        { id: `t${m.awayTeamId}`, name: m.awayName, logoUrl: teamsById[m.awayTeamId]?.logo_url || null },
      ]),
      85
    );
  }

  const big = report.matchdayMatches.biggestWin;
  if (big.value >= 3) {
    add(
      "biggest-win",
      "STATEMENT WIN",
      "good",
      `+${big.value}`,
      big.entries.map(fmtMatch).join(" · "),
      big.entries.map((m) => ({
        id: `t${m.winnerTeamId}`,
        name: teamsById[m.winnerTeamId]?.name || "",
        logoUrl: teamsById[m.winnerTeamId]?.logo_url || null,
      })),
      80
    );
  }

  add(
    "deadliest-attack",
    "DEADLIEST ATTACK",
    "gold",
    ts.mostGoalsFor.value,
    "goals scored",
    ts.mostGoalsFor.entries.map(teamEntity),
    ts.mostGoalsFor.entries.length === 1 ? 78 : 55
  );

  if (ts.biggestClimber.value) {
    add(
      "biggest-climber",
      "BIGGEST CLIMBER",
      "good",
      `▲${ts.biggestClimber.value}`,
      ts.biggestClimber.value === 1 ? "place up the table" : "places up the table",
      ts.biggestClimber.entries.map(teamEntity),
      ts.biggestClimber.entries.length <= 2 ? 82 : 50
    );
  }

  if (ts.longestWinningRun.value >= 2) {
    add(
      "winning-run",
      "ON FIRE",
      "good",
      ts.longestWinningRun.value,
      "wins in a row",
      ts.longestWinningRun.entries.map(teamEntity),
      ts.longestWinningRun.entries.length <= 2 ? 76 : 45
    );
  }

  if (ts.longestUnbeatenRun.value >= 3 && ts.longestUnbeatenRun.value > (ts.longestWinningRun.value || 0)) {
    add(
      "unbeaten-run",
      "UNBEATEN",
      "good",
      ts.longestUnbeatenRun.value,
      "games without defeat",
      ts.longestUnbeatenRun.entries.map(teamEntity),
      65
    );
  }

  const leaky = ts.mostGoalsAgainst;
  if (leaky.value >= 4) {
    add(
      "leaky-defence",
      "DEFENCE UNDER PRESSURE",
      "bad",
      leaky.value,
      "goals conceded",
      leaky.entries.map(teamEntity),
      leaky.entries.length === 1 ? 58 : 40
    );
  }

  const bestD = ts.bestDefence;
  if (bestD.value !== null && bestD.entries.length <= 3) {
    add(
      "best-defence",
      bestD.value === 0 ? "BRICK WALL" : "BEST DEFENCE",
      "good",
      bestD.value === 0 ? "0" : bestD.value.toFixed(1),
      "goals conceded per game",
      bestD.entries.map(teamEntity),
      bestD.value === 0 ? 72 : 52
    );
  }

  const mm = report.matchdayMatches;
  if (mm.counts.completed > 0) {
    add(
      "matchday-goals",
      "MATCHDAY IN NUMBERS",
      "gold",
      mm.totalGoals,
      `goals in ${mm.counts.completed} ${mm.counts.completed === 1 ? "game" : "games"}${
        mm.draws ? ` · ${mm.draws} ${mm.draws === 1 ? "draw" : "draws"}` : ""
      }${mm.counts.forfeit ? ` · ${mm.counts.forfeit} forfeit${mm.counts.forfeit === 1 ? "" : "s"}` : ""}`,
      [],
      35
    );
  }

  return out.sort((a, b) => b.weight - a.weight);
}

/**
 * Picks up to `count` highlights. A card that repeats the previous matchday's
 * card (same stat, same leaders) is pushed down so the series stays fresh.
 */
export function selectHighlights(report, previousReport, teamsById, count = 4) {
  const prevKeys = new Set(
    previousReport
      ? highlightCandidates(previousReport, teamsById)
          .slice(0, count)
          .map((c) => `${c.key}:${c.entities.map((e) => e.id).join(",")}`)
      : []
  );
  const scored = highlightCandidates(report, teamsById).map((c) => ({
    ...c,
    repeat: prevKeys.has(`${c.key}:${c.entities.map((e) => e.id).join(",")}`),
  }));
  scored.sort((a, b) => b.weight - (b.repeat ? 30 : 0) - (a.weight - (a.repeat ? 30 : 0)));
  return scored.slice(0, count);
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const SEASON_TERMS = { F: "Fall", W: "Winter" };

export function seasonLabel(code) {
  const m = /^([A-Z]+)(\d{2})$/.exec(String(code || "").trim().toUpperCase());
  if (!m || !SEASON_TERMS[m[1]]) return String(code || "");
  return `${SEASON_TERMS[m[1]]} 20${m[2]}`;
}

export function divisionLabel(division) {
  const d = String(division || "").trim();
  const m = /^DIV\s*(\d+)$/i.exec(d);
  if (m) return `Division ${m[1]}`;
  return /division/i.test(d) ? d : `${d} Division`;
}

export const pad2 = (n) => String(n).padStart(2, "0");

/** Distinct, high-contrast colours on the navy background; assigned by team id. */
export const TEAM_COLORS = [
  "#ff8a1f",
  "#f5c542",
  "#4fc3f7",
  "#ef5350",
  "#66bb6a",
  "#ba68c8",
  "#f06292",
  "#26c6da",
  "#d4e157",
  "#90a4ae",
  "#ffab91",
  "#7986cb",
];
